import { useState } from 'react';
import {
  Box, Card, Container, Skeleton, Stack, ToggleButton,
  ToggleButtonGroup, Typography, createTheme, ThemeProvider,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import GroupIcon from '@mui/icons-material/Group';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import { useQuery } from '@tanstack/react-query';
import { FetchReviewsLeaderboard } from '../../services/paymentdashboardservice';
import StatCard from '../paymenthistory/common/StatCard';
import PodiumSection from './PodiumSection';
import LeaderboardTable from './LeaderboardTable';

const theme = createTheme({
  typography: { fontFamily: ['Quicksand', 'Playfair Display'].join(','), fontSize: 16 },
});

const PAGE_BG = {
  minHeight: '100vh',
  background: 'linear-gradient(145deg, #EDF1FF 0%, #F2EEFF 38%, #FFF0ED 100%)',
  backgroundAttachment: 'fixed',
};

const CONTAINER_SX = {
  mt: '64px',
  pl: { xs: 2, sm: 3, lg: '108px' },
  pr: { xs: 2, sm: 3 },
};

const PERIODS = [
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'all_time',   label: 'All Time' },
];

const MEDAL = {
  1: { color: '#F59E0B', light: '#FFF8E1', gradient: 'linear-gradient(135deg, #FFD700 0%, #F59E0B 100%)', label: '1st Place' },
  2: { color: '#78909C', light: '#ECEFF1', gradient: 'linear-gradient(135deg, #B0BEC5 0%, #78909C 100%)', label: '2nd Place' },
  3: { color: '#BF6B20', light: '#FFF3E0', gradient: 'linear-gradient(135deg, #CD7F32 0%, #BF6B20 100%)', label: '3rd Place' },
};

function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

// ── Creative rank card replacing the plain "#2" StatCard ──────────────────
function YourRankCard({ rank, totalReviews }) {
  const medal = MEDAL[rank];
  const accentColor = medal?.color ?? '#10B981';
  const accentGradient = medal?.gradient ?? 'linear-gradient(135deg, #10B981 0%, #059669 100%)';
  const placeLabel = medal?.label ?? `${rank}${ordinal(rank)} Place`;

  return (
    <Card
      sx={{
        position: 'relative', overflow: 'hidden',
        borderRadius: 3,
        p: '20px 20px 14px',
        flex: 1, minWidth: 160,
        background: '#FFFFFF',
        boxShadow: '0 2px 16px rgba(0,0,0,0.05)',
        border: '1px solid rgba(255,82,0,0.07)',
        transition: 'transform 0.22s ease, box-shadow 0.22s ease',
        '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 8px 28px ${accentColor}28` },
      }}
    >
      {/* Accent bar */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: accentGradient, borderRadius: '3px 3px 0 0' }} />

      {/* Circular rank badge */}
      <Box
        sx={{
          width: 44, height: 44, borderRadius: '50%',
          background: accentGradient,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 4px 14px ${accentColor}40`,
          mb: 1.5,
        }}
      >
        <Typography component="span" sx={{ fontFamily: 'Quicksand', fontWeight: 900, fontSize: 18, color: '#fff', lineHeight: 1 }}>
          {rank}
        </Typography>
        <Typography component="span" sx={{ fontFamily: 'Quicksand', fontWeight: 700, fontSize: 8, color: 'rgba(255,255,255,0.85)', lineHeight: 1, letterSpacing: 0.5 }}>
          {ordinal(rank).toUpperCase()}
        </Typography>
      </Box>

      <Typography sx={{ fontSize: 10, fontWeight: 700, fontFamily: 'Quicksand', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.4 }}>
        Your Rank
      </Typography>

      <Typography sx={{ fontSize: 20, fontWeight: 900, fontFamily: 'Quicksand', color: accentColor, lineHeight: 1.1, mb: 0.4 }}>
        {placeLabel}
      </Typography>

      <Typography sx={{ fontSize: 11, fontWeight: 600, fontFamily: 'Quicksand', color: '#CBD5E1', textTransform: 'capitalize' }}>
        {totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}
      </Typography>
    </Card>
  );
}

// ── No-rank card when user hasn't reviewed ────────────────────────────────
function NoRankCard() {
  return (
    <Card
      sx={{
        position: 'relative', overflow: 'hidden', borderRadius: 3,
        p: '20px 20px 14px', flex: 1, minWidth: 160,
        background: '#FFFFFF', boxShadow: '0 2px 16px rgba(0,0,0,0.05)',
        border: '1px solid rgba(255,82,0,0.07)',
      }}
    >
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, #E5E7EB 0%, #D1D5DB 100%)', borderRadius: '3px 3px 0 0' }} />
      <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.25 }}>
        <LeaderboardIcon sx={{ fontSize: 18, color: '#D1D5DB' }} />
      </Box>
      <Typography sx={{ fontSize: 10, fontWeight: 700, fontFamily: 'Quicksand', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.4 }}>
        Your Rank
      </Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 900, fontFamily: 'Quicksand', color: '#CBD5E1', lineHeight: 1.15, mb: 0.4 }}>
        —
      </Typography>
      <Typography sx={{ fontSize: 11, fontWeight: 600, fontFamily: 'Quicksand', color: '#CBD5E1' }}>
        no reviews yet
      </Typography>
    </Card>
  );
}

function LoadingSkeleton() {
  return (
    <Box sx={PAGE_BG}>
      <ThemeProvider theme={theme}>
        <Container sx={{ ...CONTAINER_SX, pt: 3, pb: 6 }} maxWidth="xl">
          <Box sx={{ mb: 3.5 }}>
            <Skeleton variant="rounded" width={120} height={20} sx={{ borderRadius: 2, mb: 1 }} />
            <Skeleton variant="text" width={280} height={44} sx={{ borderRadius: 1 }} />
            <Skeleton variant="text" width={380} height={18} sx={{ mt: 0.5 }} />
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4 }}>
            {[1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={96} sx={{ borderRadius: 3, flex: 1 }} />)}
          </Stack>
          <Skeleton variant="rounded" height={300} sx={{ borderRadius: 4, mb: 4 }} />
          <Skeleton variant="rounded" height={360} sx={{ borderRadius: 3 }} />
        </Container>
      </ThemeProvider>
    </Box>
  );
}

function EmptyState({ period }) {
  const msgs = {
    this_month: 'No reviews have been submitted this month.',
    last_month: 'No reviews were submitted last month.',
    all_time:   'No reviews have been submitted yet.',
  };
  return (
    <Box
      sx={{
        textAlign: 'center', py: 10,
        '@keyframes fadeUp': { from: { opacity: 0, transform: 'translateY(12px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
        animation: 'fadeUp 0.38s ease both',
      }}
    >
      <EmojiEventsIcon sx={{ fontSize: 52, color: '#E5E7EB', mb: 2 }} />
      <Typography sx={{ fontFamily: 'Quicksand', fontWeight: 800, fontSize: 17, color: '#374151', mb: 0.75 }}>
        No reviews yet
      </Typography>
      <Typography sx={{ fontFamily: 'Quicksand', fontWeight: 600, fontSize: 13, color: '#9CA3AF' }}>
        {msgs[period]}
      </Typography>
    </Box>
  );
}

export default function ReviewsLeaderboardPage() {
  const [period, setPeriod] = useState('this_month');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['reviews-leaderboard', period],
    queryFn: () => FetchReviewsLeaderboard(period),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  if (isLoading) return <LoadingSkeleton />;

  const leaderboard      = data?.leaderboard ?? [];
  const meta             = data?.meta ?? {};
  const currentUserId    = data?.currentUserId ?? '';
  const currentUserEntry = leaderboard.find(e => e._id === currentUserId);
  const showStickyBanner = Boolean(currentUserEntry && currentUserEntry.rank > 10);
  const periodLabel      = PERIODS.find(p => p.value === period)?.label.toLowerCase() ?? '';

  return (
    <Box sx={PAGE_BG}>
      <ThemeProvider theme={theme}>
        <Container
          sx={{
            ...CONTAINER_SX,
            pt: 3,
            pb: showStickyBanner ? 9 : 6,
            '@keyframes pageFadeIn': { from: { opacity: 0, transform: 'translateY(8px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
            animation: 'pageFadeIn 0.36s ease both',
          }}
          maxWidth="xl"
        >
          {/* ── Creative Header ── */}
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
              mb: 3.5,
            }}
          >
            <Box>
              {/* Label badge */}
              <Box
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.6,
                  bgcolor: 'rgba(255,82,0,0.08)',
                  border: '1px solid rgba(255,82,0,0.15)',
                  borderRadius: '20px',
                  px: 1.25, py: 0.35,
                  mb: 1,
                }}
              >
                <EmojiEventsIcon sx={{ fontSize: 12, color: '#FF5200' }} />
                <Typography
                  sx={{
                    fontFamily: 'Quicksand', fontWeight: 800, fontSize: 10,
                    color: '#FF5200', textTransform: 'uppercase', letterSpacing: 1.2,
                  }}
                >
                  Reviews
                </Typography>
              </Box>

              {/* Main title with gradient */}
              <Typography
                sx={{
                  fontFamily: 'Quicksand', fontWeight: 900,
                  fontSize: { xs: 28, sm: 34, md: 38 },
                  lineHeight: 1.05, mb: 0.75,
                  background: 'linear-gradient(120deg, #1C1C2E 0%, #374151 55%, #FF5200 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Leaderboard
              </Typography>

              <Typography
                sx={{
                  fontFamily: 'Quicksand', fontWeight: 600,
                  fontSize: { xs: 11, sm: 13 }, color: '#9CA3AF',
                }}
              >
                Recognising team members who contribute the most influencer reviews
              </Typography>
            </Box>

            {/* Period toggle */}
            <ToggleButtonGroup
              value={period}
              exclusive
              onChange={(_, v) => { if (v) setPeriod(v); }}
              size="small"
              sx={{
                bgcolor: '#fff', borderRadius: 2,
                border: '1px solid rgba(0,0,0,0.08)', flexShrink: 0,
                '& .MuiToggleButton-root': {
                  fontFamily: 'Quicksand', fontWeight: 700,
                  fontSize: { xs: 11, sm: 12 }, textTransform: 'none',
                  px: { xs: 1.25, sm: 2 }, py: 0.75,
                  border: 'none', color: '#6B7280',
                  transition: 'background-color 0.18s ease, color 0.18s ease',
                  '&.Mui-selected': { bgcolor: '#FF5200', color: '#fff', '&:hover': { bgcolor: '#e04600' } },
                  '&:hover:not(.Mui-selected)': { bgcolor: 'rgba(255,82,0,0.06)' },
                },
              }}
            >
              {PERIODS.map(({ value, label }) => (
                <ToggleButton key={value} value={value}>{label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {/* ── Stat cards ── */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{
              mb: 4,
              '@keyframes fadeUp': { from: { opacity: 0, transform: 'translateY(14px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
              animation: 'fadeUp 0.34s ease 0.06s both',
            }}
          >
            <StatCard
              heading="Total Reviews"
              value={isError ? '—' : (meta.totalReviews ?? 0)}
              subtext={periodLabel}
              icon={<EmojiEventsIcon sx={{ fontSize: 18 }} />}
            />
            <StatCard
              heading="Active Reviewers"
              value={isError ? '—' : (meta.activeReviewers ?? 0)}
              subtext="team members"
              icon={<GroupIcon sx={{ fontSize: 18 }} />}
              accentColor="#6366F1"
            />
            {/* Custom rank card — replaces plain "#2" */}
            {isError || !currentUserEntry
              ? <NoRankCard />
              : <YourRankCard rank={currentUserEntry.rank} totalReviews={currentUserEntry.totalReviews} />
            }
          </Stack>

          {/* ── Main content keyed by period ── */}
          {isError ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography sx={{ fontFamily: 'Quicksand', fontWeight: 600, fontSize: 14, color: '#9CA3AF' }}>
                Failed to load leaderboard. Please try again.
              </Typography>
            </Box>
          ) : leaderboard.length === 0 ? (
            <EmptyState period={period} />
          ) : (
            <Box key={period}>
              <Box
                sx={{
                  bgcolor: 'rgba(255,255,255,0.62)', borderRadius: { xs: 3, md: 4 },
                  p: { xs: 2, sm: 3, md: 4 }, mb: 4,
                  border: '1px solid rgba(255,82,0,0.07)', backdropFilter: 'blur(10px)',
                  '@keyframes podiumSectionIn': { from: { opacity: 0, transform: 'translateY(16px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
                  animation: 'podiumSectionIn 0.4s ease 0.1s both',
                }}
              >
                <PodiumSection leaderboard={leaderboard} currentUserId={currentUserId} />
              </Box>

              <Box
                sx={{
                  '@keyframes tableSectionIn': { from: { opacity: 0, transform: 'translateY(12px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
                  animation: 'tableSectionIn 0.38s ease 0.18s both',
                }}
              >
                <LeaderboardTable leaderboard={leaderboard} currentUserId={currentUserId} />
              </Box>
            </Box>
          )}
        </Container>

        {/* ── Sticky banner ── */}
        {showStickyBanner && (
          <Box
            sx={{
              position: 'fixed', bottom: 0,
              left: { xs: 0, lg: 68 }, right: 0, zIndex: 100,
              bgcolor: '#1C1C2E', borderTop: '1px solid rgba(255,82,0,0.18)',
              py: 1.5, px: { xs: 2, sm: 4 },
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5,
              '@keyframes slideUp': { from: { transform: 'translateY(100%)' }, to: { transform: 'translateY(0)' } },
              animation: 'slideUp 0.28s ease both',
            }}
          >
            <LeaderboardIcon sx={{ fontSize: 16, color: '#FF5200', flexShrink: 0 }} />
            <Typography sx={{ fontFamily: 'Quicksand', fontWeight: 700, fontSize: { xs: 11, sm: 13 }, color: '#fff' }}>
              You're ranked{' '}
              <Box component="span" sx={{ color: '#FF5200', fontWeight: 900 }}>
                {currentUserEntry.rank}{ordinal(currentUserEntry.rank)} place
              </Box>
              {' '}with{' '}
              <Box component="span" sx={{ color: '#FF5200', fontWeight: 900 }}>{currentUserEntry.totalReviews} reviews</Box>
              {' '}— keep going!
            </Typography>
          </Box>
        )}
      </ThemeProvider>
    </Box>
  );
}
